import React from "react";
import { FiShield } from "react-icons/fi";
import {
  errorMessage,
  isPayoutMockMode,
  isPayoutStepUpEnabled,
  pickString,
  stableIdempotencyKey,
  verifyStepUp,
} from "./api/payoutMutationHelpers";

export type StepUpChallengeModalProps = {
  open: boolean;
  title: string;
  description: string;
  purpose: string;
  onClose: () => void;
  onVerified: (stepUpToken: string | undefined) => void | Promise<void>;
};

export function StepUpChallengeModal({
  open,
  title,
  description,
  purpose,
  onClose,
  onVerified,
}: StepUpChallengeModalProps) {
  const [method, setMethod] = React.useState<"sms" | "authenticator">("authenticator");
  const [code, setCode] = React.useState<string[]>(Array(6).fill(""));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  const verificationCode = code.join("");
  const ready = verificationCode.length === 6;

  React.useEffect(() => {
    if (!open) {
      setCode(Array(6).fill(""));
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  const handleVerify = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      if (isPayoutMockMode() || !isPayoutStepUpEnabled()) {
        await onVerified(undefined);
        return;
      }
      const token = await verifyStepUp({ purpose, method, code: verificationCode });
      await onVerified(token);
    } catch (err) {
      setError(errorMessage(err, "Verification failed. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-[#1B163B66] p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md rounded-[8px] border border-[#E0E5F2] bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-lg text-[#1B163B]">
            <FiShield />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-[#1B163B]">{title}</h2>
            <p className="mt-1 text-sm font-medium text-[#505E8E]">{description}</p>
          </div>
        </div>

        <div className="mb-4 flex gap-2">
          {(["authenticator", "sms"] as const).map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setMethod(option)}
              className={
                "rounded-[6px] px-3 py-2 text-xs font-semibold " +
                (method === option
                  ? "bg-[#1B163B] text-white"
                  : "border border-[#E0E5F2] bg-white text-[#505E8E]")
              }
            >
              {option === "authenticator" ? "Authenticator" : "SMS OTP"}
            </button>
          ))}
        </div>

        <div className="mb-4 flex justify-center gap-2">
          {code.map((digit, index) => (
            <input
              key={index}
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(event) => {
                const nextDigit = event.target.value.replace(/\D/g, "").slice(-1);
                setCode((current) => {
                  const next = [...current];
                  next[index] = nextDigit;
                  return next;
                });
              }}
              className="h-11 w-10 rounded-[6px] border border-[#E0E5F2] text-center text-lg font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
            />
          ))}
        </div>

        {error ? (
          <p className="mb-3 text-sm font-semibold text-[#B42318]">{error}</p>
        ) : null}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-[6px] border border-[#E0E5F2] px-4 text-sm font-semibold text-[#1B163B]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!ready || submitting}
            onClick={handleVerify}
            className="h-10 rounded-[6px] bg-[#1B163B] px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            {submitting ? "Verifying…" : "Verify"}
          </button>
        </div>
      </div>
    </div>
  );
}

export const readBackupCodes = (payload: unknown): string[] => {
  if (!payload || typeof payload !== "object") return [];
  const record = payload as Record<string, unknown>;
  const codes = record.backup_codes ?? record.backupCodes;
  return Array.isArray(codes) ? codes.map(String) : [];
};

export const readOtpAuthUri = (payload: unknown): string =>
  pickString(payload, ["otpauth_uri", "otpauthUri"], "");
